import React from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { awardArt, fetchUserInfo } from '../../utils';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';

const Page = () => {
  const { t } = useTranslation(["common", "awards"]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ★ Which event's prizes. Normally the one the visitor's session is in; `?event=<id>` names
     another, which is what the notification a revealed prize sends points at — a Bravos member's
     own context is the SEASON, not the Lab they were at last night, so without this the bell would
     open on the wrong list. Read ONCE at mount: the app strips query params a tick after they
     appear (`usePersistUrlParams`), so reading it on every render would lose it. The server checks
     that this visitor was part of that event. */
  const [eventId] = useState(
    () => new URLSearchParams(window.location.search).get('event') || '',
  );

  useEffect(() => {
    fetchUserInfo(eventId ? `/awards?event=${encodeURIComponent(eventId)}` : '/awards',
                  setUserInfo, setLoading, setError, []);
  }, [eventId]);

  const awards = userInfo?.awards || [];
  const announcedCount = awards.filter((a) => a.is_announced && a.winner_id).length;

  return (
    <Base activeNav="home">
      <Back to='/home' />
      <Title>
        <Icon name="trophy" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('awards')}
      </Title>

      {awards.length > 0 && announcedCount > 0 && (
        <p className="awards__intro mx-3 mb-3">
          {t('awardsAnnouncedSummary', { announced: announcedCount, total: awards.length })}
        </p>
      )}

      {awards.length > 0 ? (
        <div className="awards-list">
          {awards.map((award, idx) => {
            const announced = !!(award.is_announced && award.winner_id);
            const cardProps =
              announced && award.winner_type === 'team'
                ? { to: '/team', onClick: () => sessionStorage.setItem('otherTeamId', award.winner_id) }
                : announced && award.winner_type === 'company'
                ? { to: `/public-company/${award.winner_id}` }
                /* A prize with somebody's NAME on it — new in 2026-09, when running an event grew
                   the power to hand one to a person. It opens their profile, where the prize is
                   listed too. */
                : announced && award.winner_type === 'person'
                ? { to: `/u/${award.winner_id}` }
                : announced && award.winner_type === 'activity'
                ? { to: '/battle', onClick: () => sessionStorage.setItem('selectedBattleId', award.winner_id) }
                : {};

            return (
              <Card
                key={`${award.name}-${idx}`}
                mx="3"
                mb="2"
                className={`award-card ${announced ? 'award-card--announced' : 'award-card--pending'}`}
                {...cardProps}
              >
                <div className="award-card__row">
                  <div className="award-card__art">
                    {/* The picture, in one order for every screen that draws a prize: what was
                        uploaded with the award, else our bundled art by name, else the cup. */}
                    <img
                      src={awardArt(award.name, award.image)}
                      alt={t(award.name, { ns: 'awards', defaultValue: award.name })}
                    />
                    {announced && award.winner_photo_url && (
                      <span
                        className={`award-card__winner-avatar ${
                          award.winner_type === 'company' ? 'award-card__winner-avatar--contain' : ''
                        }`}
                      >
                        <Image
                          url={award.winner_photo_url}
                          size="2.6rem"
                          className="circle"
                          contain={award.winner_type === 'company'}
                        />
                      </span>
                    )}
                  </div>

                  <div className="award-card__body">
                    <div className="award-card__name">
                      {t(award.name, { ns: 'awards', defaultValue: award.name })}
                    </div>

                    {award.description && (
                      <div className="award-card__description">{award.description}</div>
                    )}

                    {announced ? (
                      <>
                        <div className="award-card__winner">{award.winner_name}</div>
                        {award.company_name && (
                          <div className="award-card__company">
                            <Icon name="building" theme="primary" className="me-1" />
                            {award.company_name}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="award-card__pending-badge">
                        <Icon name="lock" /> {t('toBeAnnounced')}
                      </span>
                    )}
                  </div>

                  {announced && cardProps.to && (
                    <Icon name="angle-right" className="award-card__chevron" />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <NotFound translationKey="noAwards" loading={loading} />
      )}
    </Base>
  );
};

export default Page;
